import { Injectable } from '@angular/core';

import { Observable, Subject, Subscription, merge, fromEvent, timer, from, interval, of } from 'rxjs';

import {
  map,
  tap,
  scan,
  take,
  filter,
  finalize,
  takeUntil,
  switchMap,
  bufferTime,
  distinctUntilChanged,
} from 'rxjs/operators';

import { IdleConfig } from '@app/models/partials.model';

/**
 * @ignore
 * User's idle service.
 */
@Injectable()
export class UserIdleService {
  ping$: Observable<any>;

  /**
   * Events that can interrupts user's inactivity timer.
   */
  protected activityEvents$: any;

  protected timerStart$ = new Subject<boolean>();
  protected timeout$ = new Subject<boolean>();
  protected idle$: Observable<any>;
  protected timer$: Observable<any>;
  /**
   * Idle value in seconds.
   * Default equals to 10 minutes.
   */
  protected idle = 780;
  /**
   * Timeout value in seconds.
   * Default equals to 2 minutes.
   */
  protected timeout = 120;
  /**
   * Ping value in seconds.
   * * Default equals to 2 minutes.
   */
  protected ping = 120;
  /**
   * Timeout status.
   */
  protected isTimeout: boolean;
  /**
   * Timer of user's inactivity is in progress.
   */
  protected isInactivityTimer: boolean;

  protected idleSubscription: Subscription;

  constructor() {
    this.activityEvents$ = merge(
      fromEvent(window, 'mousemove'),
      fromEvent(window, 'resize'),
      fromEvent(document, 'keydown')
    );

    this.idle$ = from(this.activityEvents$);
  }

  checkSubscription() {
    if (this.idleSubscription) {
      this.idleSubscription.unsubscribe();
    }
  }

  /**
   * Start watching for user idle and setup timer and ping.
   */
  startWatching() {
    this.checkSubscription();

    // If any of user events is not active for idle-seconds when start timer.
    this.idleSubscription = this.idle$
      .pipe(
        bufferTime(5000), // Starting point of detecting of user's inactivity
        filter(arr => !arr.length && !this.isInactivityTimer),
        tap(() => (this.isInactivityTimer = true)),
        switchMap(() =>
          interval(1000).pipe(
            takeUntil(
              merge(this.activityEvents$, timer(this.idle * 1000).pipe(tap(() => this.timerStart$.next(true))))
            ),
            finalize(() => (this.isInactivityTimer = false))
          )
        )
      )
      .subscribe();

    this.setupTimer(this.timeout);
    this.setupPing(this.ping);
  }

  stopWatching() {
    this.stopTimer();
    this.checkSubscription();
  }

  stopTimer() {
    this.timerStart$.next(false);
  }

  resetTimer() {
    this.stopTimer();
    this.isTimeout = false;
  }

  /**
   * Return observable for timer's countdown number that emits after idle.
   */
  onTimerStart(): Observable<number> {
    return this.timerStart$.pipe(
      distinctUntilChanged(),
      switchMap(start => (start ? this.timer$ : of(null)))
    );
  }

  /**
   * Return observable for timeout is fired.
   */
  onTimeout(): Observable<boolean> {
    return this.timeout$.pipe(
      filter(timeout => !!timeout),
      tap(() => (this.isTimeout = true)),
      map(() => true)
    );
  }

  getConfigValue(): IdleConfig {
    return {
      idle: this.idle,
      timeout: this.timeout,
      ping: this.ping,
    };
  }

  /**
   * Set config values.
   * @param config
   */
  setConfig(config: IdleConfig) {
    if (this.idleSubscription && !this.idleSubscription.closed) {
      console.error('Call stopWatching() before set config values');
      return;
    }

    if (config.idle) {
      this.idle = config.idle;
    }

    if (config.ping) {
      this.ping = config.ping;
    }

    if (config.timeout) {
      this.timeout = config.timeout;
    }
  }

  /**
   * Setup timer.
   *
   * Counts every seconds and return n+1 and fire timeout for last count.
   * @param timeout Timeout in seconds.
   */
  protected setupTimer(timeout: number) {
    this.timer$ = interval(1000).pipe(
      take(timeout),
      map(() => 1),
      scan((acc, n) => acc + n),
      tap(count => {
        if (count === timeout) {
          this.timeout$.next(true);
        }
      })
    );
  }

  /**
   * Setup ping.
   *
   * Pings every ping-seconds only if is not timeout.
   * @param ping
   */
  protected setupPing(ping: number) {
    this.ping$ = interval(ping * 1000).pipe(filter(() => !this.isTimeout));
  }
}
